import type { PlanTier } from "@/lib/authz/plans";

// Data for the pricing comparison matrix. The per-tier tool inclusion mirrors
// FEATURES_BY_VARIANT_ID in src/lib/server/sealSubscriptions.ts (the source of
// truth for what each subscription unlocks) — KEEP IN SYNC with that map.
//
// Note the structure: "Vehicle Developer" and "Server Developer" are two
// PARALLEL tracks (neither is a superset of the other). "Pro" includes both,
// and "Studio" matches Pro on tools but lifts the limits. "Free" is the demo
// on-ramp (Paint Booth only).
//
// Tool display names here must match the names used on the pricing cards
// (homepage-data.ts pricingTiers) so a customer cross-referencing doesn't hit
// spelling mismatches.

export type MatrixTier = {
  key: PlanTier;
  name: string;
  price: string;
  period: string;
  featured?: boolean;
};

export const MATRIX_TIERS: MatrixTier[] = [
  { key: "free", name: "Free", price: "$0", period: "" },
  { key: "vehicle_dev", name: "Vehicle Developer", price: "$14.99", period: "/mo" },
  { key: "server_dev", name: "Server Developer", price: "$19.99", period: "/mo" },
  { key: "all_services", name: "Pro", price: "$29.99", period: "/mo", featured: true },
  { key: "studio", name: "Studio", price: "$49.99", period: "/mo" },
];

type ToolRow = {
  name: string;
  tiers: PlanTier[];
  /** Per-tier text override for a cell (e.g. "Demo") instead of a check/dash. */
  cellLabel?: Partial<Record<PlanTier, string>>;
};
type ToolGroup = { category: string; tools: ToolRow[] };

const VEHICLE_AND_UP: PlanTier[] = ["vehicle_dev", "all_services", "studio"];
const SERVER_AND_UP: PlanTier[] = ["server_dev", "all_services", "studio"];
const ALL_TIERS: PlanTier[] = ["vehicle_dev", "server_dev", "all_services", "studio"];

export const MATRIX_GROUPS: ToolGroup[] = [
  {
    category: "Vehicle development",
    tools: [
      { name: "Paint Booth", tiers: VEHICLE_AND_UP, cellLabel: { free: "Demo" } },
      { name: "Paint Booth Studio", tiers: VEHICLE_AND_UP, cellLabel: { free: "Demo" } },
      { name: "UV Studio", tiers: VEHICLE_AND_UP },
      { name: "Blinklab", tiers: VEHICLE_AND_UP },
      { name: "3D BlinkLab", tiers: ["studio"] },
      { name: "Chevron Builder", tiers: VEHICLE_AND_UP },
      { name: "EasyMeta", tiers: ALL_TIERS },
      { name: "Handling Diff", tiers: VEHICLE_AND_UP },
    ],
  },
  {
    category: "Server development",
    tools: [
      { name: "PackBench", tiers: SERVER_AND_UP },
      { name: "Code Smith", tiers: SERVER_AND_UP },
      { name: "Code Visualizer", tiers: SERVER_AND_UP },
      { name: "Script Docs", tiers: SERVER_AND_UP },
      { name: "EUP Viewer", tiers: SERVER_AND_UP },
      { name: "Addon EUP Creator", tiers: SERVER_AND_UP },
      { name: "Weapon Designer", tiers: SERVER_AND_UP },
      { name: "Resmon Report", tiers: SERVER_AND_UP },
    ],
  },
  {
    category: "AI generation",
    tools: [
      { name: "Asset Art", tiers: SERVER_AND_UP },
      { name: "ModelForge", tiers: SERVER_AND_UP },
      { name: "Badge Maker", tiers: SERVER_AND_UP },
      { name: "Embroidery Tool", tiers: SERVER_AND_UP },
    ],
  },
  {
    category: "Workflow",
    tools: [{ name: "Creator Convert", tiers: ALL_TIERS }],
  },
];

export const MATRIX_LIMITS: { label: string; values: Partial<Record<PlanTier, string>> }[] = [
  {
    label: "Cloud storage",
    values: { vehicle_dev: "10 GB", server_dev: "25 GB", all_services: "50 GB", studio: "150 GB" },
  },
  {
    label: "AI tokens / month",
    values: { vehicle_dev: "—", server_dev: "300", all_services: "750", studio: "2,000" },
  },
  {
    label: "Max file size",
    values: { vehicle_dev: "500 MB", server_dev: "1 GB", all_services: "1 GB", studio: "2 GB" },
  },
];
